import React, { useEffect, useRef } from 'react'

import { FULFILLED } from '../../constants/constants'
import { useAppSelector } from '../../hooks'
import * as Styled from './styles'

declare global {
  interface Window {
    walley?: {
      checkout?: {
        api?: {
          update?: () => void
        }
      }
    }
  }
}

const CONTAINER_ID = 'walley-part-payment-container'

interface Props {
  insideSummaryPanel?: boolean
}

const PartPaymentWidget: React.FC<Props> = ({ insideSummaryPanel }) => {
  const partPaymentWidget = useAppSelector((state) => state.init.partPaymentWidget)
  const { summary } = useAppSelector((state) => state.bundle)
  const { requestStatus_fetchPrices_aggregated } = useAppSelector((state) => state.requests.requestStatus)

  const scriptRef = useRef<HTMLScriptElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const pricesFulfilled = requestStatus_fetchPrices_aggregated === FULFILLED
  const price = summary?.Price
  const amountInSmallestUnit = price != null ? Math.round(price * 100) : null

  const isWithinRange =
    partPaymentWidget != null &&
    amountInSmallestUnit != null &&
    amountInSmallestUnit >= partPaymentWidget.minPayment &&
    amountInSmallestUnit <= partPaymentWidget.maxPayment

  const shouldRender = pricesFulfilled && isWithinRange

  // Inject or remove the Walley loader script
  useEffect(() => {
    if (!shouldRender || !partPaymentWidget || amountInSmallestUnit == null) {
      // Remove script if it exists and we should no longer render
      if (scriptRef.current) {
        scriptRef.current.remove()
        scriptRef.current = null
      }
      return
    }

    // If script already exists, don't re-inject — amount updates are handled separately
    if (scriptRef.current) return

    const script = document.createElement('script')
    script.src = partPaymentWidget.loaderScriptUrl
    script.setAttribute('data-token', partPaymentWidget.token)
    script.setAttribute('data-widget', 'part-payment')
    script.setAttribute('data-amount', String(amountInSmallestUnit))
    script.setAttribute('data-container-id', CONTAINER_ID)

    containerRef.current?.appendChild(script)
    scriptRef.current = script

    return () => {
      if (scriptRef.current) {
        scriptRef.current.remove()
        scriptRef.current = null
      }
    }
  }, [shouldRender, partPaymentWidget, amountInSmallestUnit])

  // Update the amount when price changes (without re-injecting the script)
  useEffect(() => {
    if (!scriptRef.current || amountInSmallestUnit == null) return

    scriptRef.current.setAttribute('data-amount', String(amountInSmallestUnit))
    window.walley?.checkout?.api?.update?.()
  }, [amountInSmallestUnit])

  if (!shouldRender) return null

  return <Styled.PartPaymentWidget id={CONTAINER_ID} ref={containerRef} $insideSummaryPanel={insideSummaryPanel} />
}

export default PartPaymentWidget
